/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { CheckCircleOutline } from '@mui/icons-material'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import React from 'react'

import type { ReviewFeedback } from '@/graphql'
import useT from '@/i18n/useT'

type ReviewFeedbackNoticeProps = {
  feedback: ReviewFeedback | null | undefined
  returnedCount: number | null | undefined
  onAcknowledge?: () => void
  acknowledged?: boolean
  needsAttention?: boolean
}

function ReviewFeedbackNotice({
  feedback,
  returnedCount,
  onAcknowledge,
  acknowledged = false,
  needsAttention = false,
}: ReviewFeedbackNoticeProps) {
  const { t } = useT()
  const count = returnedCount ?? 0
  if (!feedback && count <= 0) return null

  const note =
    feedback?.note?.trim() ||
    t('labeling.returnedFeedback.noNote', 'No feedback provided')

  const hasFeedback = Boolean(feedback)
  const showAcknowledgeButton = Boolean(onAcknowledge)

  return (
    <Box
      sx={
        needsAttention
          ? {
              borderRadius: 1,
              '@keyframes feedbackPulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 transparent' },
                '30%': { boxShadow: '0 0 0 4px rgba(237, 108, 2, 0.55)' },
                '70%': { boxShadow: '0 0 0 4px rgba(237, 108, 2, 0.55)' },
              },
              animation: 'feedbackPulse 1s ease-in-out',
            }
          : undefined
      }
    >
      <Alert
        severity={acknowledged ? 'success' : hasFeedback ? 'warning' : 'info'}
        sx={{ width: '100%' }}
        icon={
          acknowledged ? <CheckCircleOutline fontSize="inherit" /> : undefined
        }
      >
        <Stack spacing={0.75}>
          <Typography variant="body2">
            {t('labeling.returnedFeedback.count', {
              count,
              defaultValue: `${count} ${count === 1 ? 'item' : 'items'} returned by reviewer`,
            })}
          </Typography>
          {feedback ? (
            <Typography variant="body2">
              {feedback.reviewerEmail
                ? t('labeling.returnedFeedback.noteWithReviewer', {
                    reviewer: feedback.reviewerEmail,
                    note,
                    defaultValue: `${feedback.reviewerEmail}: ${note}`,
                  })
                : note}
            </Typography>
          ) : null}
          {showAcknowledgeButton ? (
            acknowledged ? (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('labeling.returnedFeedback.acknowledged', 'Acknowledged')}
              </Typography>
            ) : (
              <Box>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={onAcknowledge}
                  sx={{ mt: 0.5 }}
                >
                  {t(
                    'labeling.returnedFeedback.acknowledgeButton',
                    'Acknowledge feedback',
                  )}
                </Button>
              </Box>
            )
          ) : null}
        </Stack>
      </Alert>
    </Box>
  )
}

export default ReviewFeedbackNotice
